// src/careJobs/components/jobCard.tsx
import * as React from "react";
import {
  Card, CardContent, Stack, Typography, Chip, Button, Tooltip, Box, Link as MuiLink,
} from "@mui/material";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import { Link as RouterLink } from "react-router-dom";

// ✅ Inline applied-state support when caller doesn't pass it
import useJobActions from "../hooks/useJobActions";
import formatCityStateZip from "../../../../platform/careComponents/utils/formatCityStateZip";

/* ======================= Types ======================= */
export type JobCardBusiness = {
  id?: number | string | null;
  name?: string | null;
  url?: string | null;  // business slug
  address?: string | null;
};
export type JobCardJob = {
  id?: number | string | null;
  title?: string | null;
  description?: string | null;
  employmentType?: string | null;
  location?: string | null;
  payMin?: number | null;
  payMax?: number | null;
  payUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR" | string | null;
  applicationsCount?: number | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  business?: JobCardBusiness | null;
};
export type JobCardProps = {
  job: JobCardJob;
  /**
   * If present, we assume this card is being rendered in the Applications view,
   * and we auto-hide Apply.
   */
  status?: string | null;

  /** Toggles for header actions */
  showShare?: boolean;
  showApply?: boolean;
  showSave?: boolean;

  /** Action handlers (optional) */
  onShare?: (job: JobCardJob) => void;
  onApply?: (job: JobCardJob) => void;
  onToggleSave?: (job: JobCardJob, nextSaved: boolean) => void;

  /** Saved state inputs */
  isSaved?: boolean;
  /**
   * Whether the saved state is known yet.
   * If false, we hide Save to avoid flicker.
   */
  savedKnown?: boolean;

  /** Applied state inputs (optional; if omitted we auto-detect) */
  /**
   * Whether the current user has already applied to this job.
   * If true, the Apply button is hidden.
   */
  isApplied?: boolean;
  /**
   * Whether the applied state is known yet.
   * If false, we hide Apply to avoid flicker/incorrect UI.
   */
  appliedKnown?: boolean;

  /** Content controls */
  showDescription?: boolean;
  maxDescriptionLines?: number;
  rightHeaderExtras?: React.ReactNode;
  applyLabel?: string;
  shareLabel?: string;
  "data-testid"?: string;
};

/* ======================= Helpers ======================= */
const stripHtml = (s?: string | null) =>
  s ? String(s).replace(/<[^>]*>/g, "") : "";

const formatCompensation = (j: Partial<JobCardJob>) => {
  const { payMin, payMax, payUnit } = j || {};
  if (payMin == null && payMax == null) return null;
  const unitShort =
    ({ HOUR: "hr", DAY: "day", MONTH: "mo", YEAR: "yr" } as any)[
      payUnit || ""
    ] || (typeof payUnit === "string" ? payUnit.toLowerCase() : "hr");
  const fmt = (n: number) => `$${Number(n).toLocaleString()}`;
  if (payMin != null && payMax != null)
    return `${fmt(payMin)}–${fmt(payMax)}/${unitShort}`;
  if (payMin != null) return `${fmt(payMin)}+/${unitShort}`;
  if (payMax != null) return `Up to ${fmt(payMax)}/${unitShort}`;
  return null;
};

const postedLabel = (j: Partial<JobCardJob>) => {
  const ts = j?.publishedAt || j?.createdAt;
  if (!ts) return null;
  try {
    return `Posted ${new Date(ts).toLocaleDateString()}`;
  } catch {
    return null;
  }
};

const CollapsibleDescription: React.FC<{
  text?: string | null;
  maxLines?: number;
}> = ({ text, maxLines = 3 }) => {
  const [open, setOpen] = React.useState(false);
  const cleaned = stripHtml(text).trim();
  if (!cleaned) return null;
  const tooLong =
    cleaned.length > 180 ||
    cleaned.split(/\s+/).length > 30 ||
    cleaned.includes("\n");
  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={
          open
            ? { whiteSpace: "pre-line" }
            : {
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "pre-line",
              }
        }
      >
        {cleaned}
      </Typography>
      {tooLong && (
        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          sx={{ mt: 0.5, px: 0, minWidth: 0, textTransform: "none" }}
        >
          {open ? "Show less" : "Read more"}
        </Button>
      )}
    </Box>
  );
};

const buildShareUrl = (job: JobCardJob) => {
  try {
    const u = new URL(window.location.href);
    if (job?.id != null) u.searchParams.set("job", String(job.id));
    return u.toString();
  } catch {
    return window.location.href;
  }
};

const defaultShare = async (j: JobCardJob) => {
  const url = buildShareUrl(j);
  const pay = formatCompensation(j);
  const title = (j?.title && String(j.title).trim()) || "Job";
  const text = pay ? `${title} • ${pay}` : title;
  try {
    if ((navigator as any).share)
      await (navigator as any).share({ title, text, url });
    else if (navigator.clipboard) await navigator.clipboard.writeText(url);
    else window.prompt("Copy this link:", url);
  } catch {}
};

const BusinessLine: React.FC<{ biz?: JobCardBusiness | null }> = ({ biz }) => {
  const address = biz?.address ? String(biz.address).trim() : "";
  const cityStateZip = React.useMemo(
    () => formatCityStateZip(address),
    [address]
  );
  if (!biz?.name) return null;
  const name = String(biz.name).trim();

  const slug = biz.url && String(biz.url).trim();
  const hasSlug = !!slug;
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mt: 0.25, display: "block", wordBreak: "break-word" }}
    >
      {hasSlug ? (
        <MuiLink component={RouterLink} to={`/b/${slug}`} underline="hover">
          {name}
        </MuiLink>
      ) : (
        name
      )}
      {address ? ` • ${cityStateZip}` : ""}
    </Typography>
  );
};

const StatusChip: React.FC<{ status?: string | null }> = ({ status }) => {
  if (!status) return null;
  const s = String(status).toUpperCase();
  const color =
    s === "HIRED"
      ? "success"
      : s === "OFFERED"
      ? "primary"
      : s === "INTERVIEW"
      ? "warning"
      : s === "REVIEWING"
      ? "info"
      : s === "REJECTED"
      ? "error"
      : "default";
  const label =
    s === "NEW"
      ? "Submitted"
      : s === "REVIEWING"
      ? "In Review"
      : s === "INTERVIEW"
      ? "Interview"
      : s === "OFFERED"
      ? "Offer Sent"
      : s === "HIRED"
      ? "Hired"
      : s === "REJECTED"
      ? "Not Selected"
      : s;
  return (
    <Chip size="small" color={color as any} variant="outlined" label={label} />
  );
};

/* ======================= Component ======================= */
const JobCard: React.FC<JobCardProps> = ({
  job,
  status,

  // action toggles
  showShare = true,
  showApply = true,
  showSave = true,

  // handlers
  onShare = defaultShare,
  onApply,
  onToggleSave,

  // saved state inputs
  isSaved,
  savedKnown = true,

  // applied state inputs (optional; can be omitted, we auto-detect)
  isApplied,
  appliedKnown,

  // content controls
  showDescription = true,
  maxDescriptionLines = 3,
  rightHeaderExtras,
  applyLabel = "Apply",
  shareLabel = "Share",
  "data-testid": testId,
}) => {
  const safeTitle = (job?.title && String(job.title).trim()) || "Untitled role";
  const canShare = !!(showShare && job && job.id != null);

  // ✅ pull applied info from hook if caller didn't pass it
  const { isApplied: hookIsApplied, appliedKnown: hookAppliedKnown } =
    useJobActions();

  // Prefer props (if provided), else fall back to hook
  const derivedAppliedKnown =
    typeof appliedKnown === "boolean" ? appliedKnown : hookAppliedKnown;

  const derivedIsApplied =
    typeof isApplied === "boolean"
      ? isApplied
      : hookAppliedKnown && job?.id != null
      ? hookIsApplied(job)
      : false;

  /**
   * Local "saved" mirror:
   * - Initializes from isSaved to ensure correct first render
   * - Updates if parent changes isSaved later (e.g., after refetch)
   */
  const [saved, setSaved] = React.useState<boolean>(!!isSaved);
  React.useEffect(() => {
    if (typeof isSaved === "boolean") setSaved(isSaved);
  }, [isSaved]);

  /**
   * Save button visibility:
   * - enabled via props (showSave)
   * - requires job.id
   * - hide while savedKnown is false (avoid flicker)
   * - hide if already saved
   */
  const shouldShowSave = Boolean(
    showSave && job?.id != null && savedKnown && !saved
  );

  const handleSave = React.useCallback(() => {
    setSaved(true);
    onToggleSave?.(job, true); // boolean ✔
  }, [onToggleSave, job]);

  /**
   * Apply button visibility:
   * - enabled via props (showApply)
   * - requires job.id
   * - hide if we are in an Applications context (status present)
   * - hide while appliedKnown is false (avoid flicker)
   * - hide if already applied
   */
  const shouldShowApply = Boolean(
    showApply &&
      job?.id != null &&
      !status && // if status is present, it's an application; hide Apply
      derivedAppliedKnown && // avoid flicker while unknown
      !derivedIsApplied // already applied? hide Apply
  );

  const comp = formatCompensation(job || {});
  const posted = postedLabel(job || {});

  return (
    <Card variant="outlined" data-testid={testId}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* ===== Header: actions top-right on sm+, stacked on xs ===== */}
        {/* ===== Header: title + actions responsive ===== */}
        <Box sx={{ mb: 0.75 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            flexWrap="wrap" // 👈 allows wrapping to next line when needed
            useFlexGap
            sx={{
              rowGap: 1, // spacing between rows if it wraps
            }}
          >
            {/* LEFT — title + business */}
            <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                }}
              >
                {safeTitle}
              </Typography>
              <BusinessLine biz={job?.business} />
            </Box>

            {/* RIGHT — actions (will wrap above if space is too tight) */}
            <Box
              sx={{
                flex: "0 0 auto",
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "flex-end",
              }}
            >
              {rightHeaderExtras}
              {!!status && <StatusChip status={status} />}
              {!status && derivedAppliedKnown && derivedIsApplied && (
                <Chip size="small" color="success" variant="outlined" label="Applied" />
              )}

              {shouldShowSave && (
                <Button variant="outlined" size="small" onClick={handleSave}>
                  Save
                </Button>
              )}
              {!status && savedKnown && saved && (
                <Chip size="small" color="info" variant="outlined" label="Saved" />
              )}

              {canShare && (
                <Tooltip title="Share this job">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<ShareRoundedIcon />}
                      onClick={() => onShare?.(job)}
                      size="small"
                    >
                      {shareLabel}
                    </Button>
                  </span>
                </Tooltip>
              )}

              {shouldShowApply && (
                <Button
                  variant="contained"
                  onClick={() => onApply?.(job)}
                  size="small"
                >
                  {applyLabel}
                </Button>
              )}
            </Box>
          </Stack>
        </Box>

        {/* Meta chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {!!job?.employmentType && (
            <Chip
              size="small"
              icon={<WorkOutlineRoundedIcon />}
              label={job.employmentType}
              variant="outlined"
            />
          )}
          {!!job?.location && (
            <Chip
              size="small"
              icon={<RoomOutlinedIcon />}
              label={job.location}
              variant="outlined"
            />
          )}
          {!!comp && (
            <Chip
              size="small"
              icon={<MonetizationOnOutlinedIcon />}
              label={comp}
              variant="outlined"
            />
          )}
          {typeof job?.applicationsCount === "number" && (
            <Chip
              size="small"
              icon={<GroupOutlinedIcon />}
              label={`${job.applicationsCount} applicant${
                job.applicationsCount === 1 ? "" : "s"
              }`}
              variant="outlined"
            />
          )}
          {!!posted && (
            <Chip
              size="small"
              icon={<EventOutlinedIcon />}
              label={posted}
              variant="outlined"
            />
          )}
        </Stack>

        {showDescription && (
          <CollapsibleDescription
            text={job?.description}
            maxLines={maxDescriptionLines}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default JobCard;
